import {
  FEEDBACK_ATTACHMENT_ALLOWED_MIME_TYPES,
  FEEDBACK_ATTACHMENT_BUCKET,
  FEEDBACK_ATTACHMENT_MAX_BYTES,
  type FeedbackAttachment,
  type FeedbackAttachmentDraft,
} from '@shared/feedbackAttachments';
import { supabase } from '../../../shared/supabase/supabaseClient';

const attachmentSelect =
  'id, ticket_id, thread_message_id, author, storage_path, original_filename, mime_type, size_bytes, created_at';

function extensionForMimeType(mimeType: string): string {
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType === 'image/jpeg') return 'jpg';
  if (mimeType === 'image/heic') return 'heic';
  if (mimeType === 'image/webp') return 'webp';
  return 'png';
}

function assertDraft(draft: FeedbackAttachmentDraft): void {
  if (!FEEDBACK_ATTACHMENT_ALLOWED_MIME_TYPES.includes(draft.mimeType)) {
    throw new Error('Solo podés adjuntar imágenes o PDF.');
  }
  if (!Number.isFinite(draft.size) || draft.size <= 0 || draft.size > FEEDBACK_ATTACHMENT_MAX_BYTES) {
    throw new Error('Cada archivo puede pesar hasta 5 MB.');
  }
}

async function readUriAsBlob(uri: string): Promise<Blob> {
  const response = await fetch(uri);
  if (!response.ok) throw new Error('No pudimos leer el archivo seleccionado.');
  return response.blob();
}

export async function uploadSupportTicketAttachments(params: {
  ticketId: string;
  author: 'commerce' | 'staff';
  drafts: FeedbackAttachmentDraft[];
  threadMessageId?: string | null;
}): Promise<FeedbackAttachment[]> {
  if (params.drafts.length === 0) return [];
  const uploaded: string[] = [];
  const insertedIds: string[] = [];
  const rows: FeedbackAttachment[] = [];
  try {
    for (const draft of params.drafts) {
      assertDraft(draft);
      const attachmentId = crypto.randomUUID();
      const path = `${params.ticketId}/${attachmentId}.${extensionForMimeType(draft.mimeType)}`;
      const blob = await readUriAsBlob(draft.uri);
      const actualSize = blob.size || draft.size;
      if (actualSize <= 0 || actualSize > FEEDBACK_ATTACHMENT_MAX_BYTES) {
        throw new Error('Cada archivo puede pesar hasta 5 MB.');
      }
      const { error: uploadError } = await supabase.storage
        .from(FEEDBACK_ATTACHMENT_BUCKET)
        .upload(path, blob, { contentType: draft.mimeType, upsert: false });
      if (uploadError) throw uploadError;
      uploaded.push(path);

      const { data, error: rowError } = await supabase
        .from('support_ticket_attachments')
        .insert({
          id: attachmentId,
          ticket_id: params.ticketId,
          thread_message_id: params.threadMessageId ?? null,
          author: params.author,
          storage_path: path,
          original_filename: draft.name.slice(0, 255),
          mime_type: draft.mimeType,
          size_bytes: actualSize,
        })
        .select(attachmentSelect)
        .single();
      if (rowError) throw rowError;
      insertedIds.push(attachmentId);
      rows.push(data as FeedbackAttachment);
    }
    return rows;
  } catch (error) {
    if (insertedIds.length > 0) {
      try {
        await supabase.from('support_ticket_attachments').delete().in('id', insertedIds);
      } catch {}
    }
    if (uploaded.length > 0) {
      await supabase.storage.from(FEEDBACK_ATTACHMENT_BUCKET).remove(uploaded).catch(() => undefined);
    }
    throw error;
  }
}

export async function listSupportTicketAttachments(ticketId: string): Promise<FeedbackAttachment[]> {
  const { data, error } = await supabase
    .from('support_ticket_attachments')
    .select(attachmentSelect)
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) throw error;

  return Promise.all(
    ((data ?? []) as FeedbackAttachment[]).map(async (attachment) => {
      const { data: signed, error: signedError } = await supabase.storage
        .from(FEEDBACK_ATTACHMENT_BUCKET)
        .createSignedUrl(attachment.storage_path, 300);
      return { ...attachment, signed_url: signedError ? null : signed?.signedUrl ?? null };
    }),
  );
}
